import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { firmContactApi, MarketCity } from '../../utils/api/firmcontact';
import { useFirmContact } from '../../utils/firmContact';

// Multi-tenant package "firmcontact" — Admin pieces:
//   FirmMarketFields  (Admin → Companies → <firm>)  the firm's market region and its other lead-posting sites
//   MarketCitiesCard  (Admin → Companies, workspace) AI Marketing's cities and their sub-areas
//   ClientRoleHint    (Admin → Users, client login)  names the portal site from the accounting firm's website
const input: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
};
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 20, marginBottom: 16 };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer' });
const hint: React.CSSProperties = { fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 };
const errBox: React.CSSProperties = { fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12 };

function SaveRow({ msg, saving, onSave }: { msg: string; saving: boolean; onSave: () => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 14 }}>
      {msg && <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" style={{ marginRight: 4 }} />{msg}</span>}
      <button onClick={onSave} disabled={saving} style={btn('var(--accent)')}>{saving ? t('login_wait') : t('save')}</button>
    </div>
  );
}

/** One firm's market region (the town / area it serves): AI prompts' "where we are", AI Marketing's home city. */
export function FirmMarketFields({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const [region, setRegion] = useState('');
  const [sites, setSites] = useState('');
  const [err, setErr] = useState(''); const [msg, setMsg] = useState(''); const [saving, setSaving] = useState(false);
  useEffect(() => {
    let alive = true;
    firmContactApi.company(companyId).then(c => { if (alive) { setRegion(c.marketRegion || ''); setSites((c.leadSites || []).join(', ')); } }).catch((e: any) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [companyId]);
  const save = async () => {
    setSaving(true); setErr(''); setMsg('');
    try {
      const c = await firmContactApi.saveCompany(companyId, { marketRegion: region, leadSites: sites });
      setRegion(c.marketRegion || ''); setSites((c.leadSites || []).join(', ')); setMsg(t('cs_saved')); setTimeout(() => setMsg(''), 2000);
    }
    catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };
  return (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('fc_market_title')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14 }}>{t('fc_market_hint')}</div>
      {err && <div style={errBox}>{err}</div>}
      <label style={lbl}>{t('fc_market_region')}</label>
      <input value={region} onChange={e => setRegion(e.target.value)} style={input} maxLength={80} placeholder="…" />
      <div style={hint}>{t('fc_market_region_hint')}</div>
      <label style={{ ...lbl, marginTop: 12 }}>{t('fc_lead_sites')}</label>
      <input value={sites} onChange={e => setSites(e.target.value)} style={input} placeholder="example.com, …" />
      <div style={hint}>{t('fc_lead_sites_hint')}</div>
      <SaveRow msg={msg} saving={saving} onSave={save} />
    </div>
  );
}

/** AI Marketing's cities (first = default) and each city's sub-areas, one per line. */
export function MarketCitiesCard() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<{ city: string; areas: string }[]>([]);
  const [effective, setEffective] = useState<MarketCity[]>([]);
  const [err, setErr] = useState(''); const [msg, setMsg] = useState(''); const [saving, setSaving] = useState(false);
  const show = (d: { saved: MarketCity[]; effective: MarketCity[] }) => {
    setRows(d.saved.map(c => ({ city: c.city, areas: c.areas.join('\n') })));
    setEffective(d.effective);
  };
  useEffect(() => { firmContactApi.marketCities().then(show).catch((e: any) => setErr(e.message)); }, []);
  const setRow = (i: number, k: 'city' | 'areas', v: string) => setRows(prev => prev.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const save = async () => {
    setSaving(true); setErr(''); setMsg('');
    try {
      const cities = rows.map(r => ({ city: r.city.trim(), areas: r.areas.split(/\n/).map(a => a.trim()).filter(Boolean) })).filter(r => r.city);
      show(await firmContactApi.saveMarketCities(cities)); setMsg(t('cs_saved')); setTimeout(() => setMsg(''), 2000);
    } catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };
  return (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('fc_cities_title')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14 }}>{t('fc_cities_hint')}</div>
      {err && <div style={errBox}>{err}</div>}
      {!rows.length && (
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10 }}>
          {effective.length ? t('fc_cities_from_firms', { cities: effective.map(c => c.city).join(', ') }) : t('fc_cities_none')}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr auto', gap: 8, alignItems: 'start' }}>
            <div>
              <label style={lbl}>{i === 0 ? t('fc_city_default') : t('fc_city')}</label>
              <input value={r.city} onChange={e => setRow(i, 'city', e.target.value)} style={input} maxLength={80} />
            </div>
            <div>
              <label style={lbl}>{t('fc_city_areas')}</label>
              <textarea value={r.areas} onChange={e => setRow(i, 'areas', e.target.value)} rows={3} style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }} placeholder={t('fc_city_areas_ph')} />
            </div>
            <button onClick={() => setRows(prev => prev.filter((_, j) => j !== i))} title={t('delete')} style={{ marginTop: 22, border: 'none', background: 'none', color: '#E24B4A', cursor: 'pointer' }}><i className="ti ti-trash" /></button>
          </div>
        ))}
      </div>
      <button onClick={() => setRows(prev => [...prev, { city: '', areas: '' }])} style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), marginTop: 10 }}>
        <i className="ti ti-plus" style={{ fontSize: 13, marginRight: 6 }} aria-hidden />{t('fc_city_add')}
      </button>
      <SaveRow msg={msg} saving={saving} onSave={save} />
    </div>
  );
}

/** "This is a client-portal login (<site>/portal) …" — the site is the accounting firm's website. */
export function ClientRoleHint() {
  const { t } = useTranslation();
  const acct = useFirmContact('accounting');
  return <>{acct?.siteHost ? t('fc_client_role_hint', { site: `${acct.siteHost}/portal` }) : t('fc_client_role_hint_plain')}</>;
}
